import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useEffect, useState } from 'react'
import {
  SearchSelectOption,
  SearchSelect,
} from '@app/components/core/input/searchSelect/SearchSelect'
import { useClient } from '@based/sdk/react'
import { Style } from 'inlines'

export type LocalitySelectorProps = {
  label?: string
  labelStyle?: Style
  displayLabelAsterisk?: boolean
  locality: SearchSelectOption
  filterByRegionId?: number
  setLocality: (locality: SearchSelectOption) => void
}

export const LocalitySelector = ({
  label,
  labelStyle,
  displayLabelAsterisk,
  locality,
  filterByRegionId,
  setLocality,
}: LocalitySelectorProps) => {
  const translations = useTranslations().app.common

  const [localityQuery, setLocalityQuery] = useState(locality.value)
  const [localityOptions, setLocalityOptions] = useState<SearchSelectOption[]>(
    [],
  )
  const [renderNotFoundMessage, setRenderNotFoundMessage] = useState(false)

  const client = useClient()
  useEffect(() => {
    if (localityQuery === locality.value) {
      return
    }
    const close = client
      .query('suggestions-locality', {
        searchTerm: localityQuery,
        regionId: filterByRegionId ?? null,
        top: 7,
      })
      .subscribe((r) => {
        const result: SearchSelectOption[] = r.map(
          (m: { id: any; name: any }) => ({
            key: m.id,
            value: m.name,
          }),
        )
        if (
          result.length === 1 &&
          result[0].key === locality.key &&
          result[0].value === localityQuery
        ) {
          setLocalityOptions([])
          setRenderNotFoundMessage(false)
        } else if (result.length === 0) {
          setLocalityOptions([])
          setRenderNotFoundMessage(true)
        } else {
          setLocalityOptions(result)
          setRenderNotFoundMessage(false)
        }
      })
    return () => close()
  }, [client, localityQuery, locality, filterByRegionId])

  useEffect(() => {
    setLocalityQuery(locality.value)
    setLocalityOptions([])
  }, [locality])

  const selectLocationOption = useCallback(
    (option: SearchSelectOption) => {
      setLocalityQuery(option.value)
      setLocality(option)
      setLocalityOptions([])
      setRenderNotFoundMessage(false)
    },
    [setLocality],
  )

  const resetLocationSelection = useCallback(() => {
    setLocalityQuery(locality.value)
    setLocalityOptions([])
  }, [locality])

  return (
    <SearchSelect
      label={label ?? translations.location}
      labelStyle={labelStyle}
      displayLabelAsterisk={displayLabelAsterisk}
      value={localityQuery}
      setValue={setLocalityQuery}
      selectOption={selectLocationOption}
      abandonSelection={resetLocationSelection}
      options={localityOptions}
      noResultsMessage={{
        display: renderNotFoundMessage,
        message: translations.locationNotFoundMessage,
      }}
    />
  )
}
